import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { cloneDeep, isEmpty, map, sumBy } from 'lodash';
import { INDIVIDUAL_POP_UP_MODE, USER_ENGAGEMENT_ROLES, USER_ENGAGEMENT_SOURCES } from '@/constants';
import { Getter } from '@/decorators';
import { isValidNumericInputKey } from '@/helpers';
import { ActivityHourMapDTO, ActivityKeyMapDTO, HRMUserEngagementTimeSheetDTO, UserDTO, UserEngagementDTO } from '@/models';
import UserInformation from '@/components/common/user-information/UserInformation.vue';
import UserInput from '@/components/common/user-input/UserInput.vue';
import RadioSwitch from '@/components/common/radio-switch/RadioSwitch.vue';
import ManuallyInput from './ManuallyInput.vue';
import HrmDataTable from '@/components/common/hrm-data-table/HrmDataTable.vue';
@Component({
  components: { UserInformation, ManuallyInput, HrmDataTable, RadioSwitch, UserInput }
})
export default class IndividualPopup extends Vue {
  @Getter('user/users') users: UserDTO[];
  @Prop({ default: () => [] }) members: UserEngagementDTO[];
  @Prop({ default: {} }) memberToEdit: UserEngagementDTO;
  @Prop({ default: false }) isEditing: boolean;
  @Prop({ default: INDIVIDUAL_POP_UP_MODE.HIDE }) mode: string;
  @Prop({ default: () => [] }) hrmMemberTimeSheets: HRMUserEngagementTimeSheetDTO[];
  @Prop({ default: () => ({}) }) activityKeyMap!: ActivityKeyMapDTO;
 
  $refs!: {
    formRef: HTMLFormElement;
  };

  dialogShown = true;
  member = { user: { id: -1 } } as UserEngagementDTO;
  currentMember: UserEngagementDTO = {} as UserEngagementDTO;
  totalHour = this.currentMember.contribution;
  currentMemberUserId = -1;
  inputOptions = {
    hrm: 'Use HRM Input',
    manuallyInput: 'Manually Input'
  };
  currentInputOption = this.inputOptions.manuallyInput;
  updatedHrmTimeSheets: HRMUserEngagementTimeSheetDTO[] = [];

  get isViewMode(): boolean {
    return this.mode === INDIVIDUAL_POP_UP_MODE.VIEW;
  }

  get isCurrentMemberImported(): boolean {
    return this.currentMember?.source === USER_ENGAGEMENT_SOURCES.IMPORTED_FROM_FILE;
  }

  get isEditMode(): boolean {
    return this.mode === INDIVIDUAL_POP_UP_MODE.EDIT;
  }

  get isAddNewMode(): boolean {
    return this.mode === INDIVIDUAL_POP_UP_MODE.ADD;
  }

  get radioButtonShown(): boolean {
    return this.isEditMode && !isEmpty(this.hrmMemberTimeSheetByMember);
  }

  get userInputRules(): ((userId: number) => boolean | string)[] {
    return [
      (userId: number) => {
        if (this.mode === INDIVIDUAL_POP_UP_MODE.ADD) {
          const user = this.members.find(member => member.user.id === userId);
          return user ? 'Employee already in team' : true;
        }
        return userId > 0 ? true : 'Name is required';
      }
    ];
  }

  get hrmMemberTimeSheetByMember(): HRMUserEngagementTimeSheetDTO[] {
    if (this.hrmMemberTimeSheets) {
      return this.hrmMemberTimeSheets.filter(({ user }) => user.id === this.currentMember.user.id);
    }
    return [];
  }

  get originalTotalHours(): number {
    if (this.currentMember) {
      return sumBy(map(this.hrmMemberTimeSheetByMember, 'totalHourTask'));
    }
    return 0;
  }

  isHrmSource(source?: string): boolean {
    return (
      source === USER_ENGAGEMENT_SOURCES.HRM_DATA ||
      source === USER_ENGAGEMENT_SOURCES.HRM_DATA_EDITED ||
      source === USER_ENGAGEMENT_SOURCES.HRM_DATA_ADD_NEW
    );
  }

  get sourceByModeAndMember(): string {
    if (this.currentMember) {
      const { contribution } = this.currentMember;
      const { hrm, manuallyInput } = this.inputOptions;
      switch (this.currentInputOption) {
        case hrm:
          if (this.originalTotalHours === contribution) {
            return USER_ENGAGEMENT_SOURCES.HRM_DATA;
          } else {
            return USER_ENGAGEMENT_SOURCES.HRM_DATA_EDITED;
          }
        case manuallyInput:
          if (this.currentMember.source === USER_ENGAGEMENT_SOURCES.IMPORTED_FROM_FILE) {
            return USER_ENGAGEMENT_SOURCES.IMPORTED_FROM_FILE;
          }
          return USER_ENGAGEMENT_SOURCES.MANUALLY_INPUT;
        default:
          return USER_ENGAGEMENT_SOURCES.MANUALLY_INPUT;
      }
    }
    return USER_ENGAGEMENT_SOURCES.MANUALLY_INPUT;
  }

  get listUsersByMode(): UserDTO[] {
    const users = map(cloneDeep(this.members), 'user');
    return !this.isAddNewMode ? users : [];
  }

  @Watch('currentMember', { deep: true })
  onCurrentMemberChanged(): void {
    this.currentMemberUserId = this.currentMember?.user?.id;
  }

  formValidate(): boolean {
    return this.$refs.formRef.validate();
  }

  getTeamMemberIds(): number[] {
    const teamMemberIds = this.members.map(member => member.user.id);
    if (this.isEditing) {
      return teamMemberIds.filter(memberId => memberId !== this.member.user.id);
    }
    return teamMemberIds;
  }

  getUserEngagement(): UserEngagementDTO {
    const contribution =
      this.inputOptions.manuallyInput === this.currentInputOption ? this.currentMember.contribution : this.totalHour;
    return {
      ...this.member,
      user: this.currentMember.user,
      contribution,
      individualDrops: this.currentMember.individualDrops,
      key: this.currentMember.key,
      userEngagementRole: USER_ENGAGEMENT_ROLES.PROJECT_MEMBER,
      note: this.currentMember.note,
      source: this.sourceByModeAndMember,
      hrmTimeSheets: this.updatedHrmTimeSheets,
      manuallyInput: this.isCurrentMemberImported ? undefined : this.currentMember.manuallyInput
    };
  }

  getUserInformation(userId: number): UserDTO {
    return this.users.find(user => user.id === userId) || ({} as UserDTO);
  }

  onCancel(): void {
    this.dialogShown = false;
    this.$emit('close');
  }

  onKeyDown(event: KeyboardEvent): void {
    if (!isValidNumericInputKey(event)) {
      event.preventDefault();
    }
  }

  submitTeamMember(): void {
    if (this.formValidate()) {
      this.$emit('onSubmitMember', this.getUserEngagement());
      this.onCancel();
    }
  }

  onUserIdChanged(user: UserDTO): void {
    if (this.mode !== INDIVIDUAL_POP_UP_MODE.ADD) {
      const member = this.members.find(member => member.user.id === user.id);
      if (member) {
        const isImported = member.source === USER_ENGAGEMENT_SOURCES.IMPORTED_FROM_FILE;
        this.currentMember = {
          ...cloneDeep(member),
          manuallyInput: isImported
            ? undefined
            : Object.entries(this.activityKeyMap).reduce((acc, [activityName, keyValue]) => {
                acc[activityName] = member.manuallyInput?.[activityName] || 0;
                return acc;
              }, {} as ActivityHourMapDTO)
        };
      }
    } else {
      this.currentMember.user = user;
    }
  }

  onModePropPassed(): void {
    const { manuallyInput, hrm: HRM } = this.inputOptions;
    if (this.mode === INDIVIDUAL_POP_UP_MODE.ADD) {
      this.currentInputOption = manuallyInput;
    } else {
      if (!isEmpty(this.currentMember)) {
        this.currentInputOption = this.isHrmSource(this.currentMember.source) ? HRM : manuallyInput;
      }
    }
  }

  onHrmUserEngagementSelected(totalHour: number, hrmMemberTimeSheets: HRMUserEngagementTimeSheetDTO[]): void {
    if (hrmMemberTimeSheets && hrmMemberTimeSheets.length > 0) {
      const user = hrmMemberTimeSheets[0].user;
      this.totalHour = totalHour;
      this.memberToEdit.user = user;
      this.updatedHrmTimeSheets = hrmMemberTimeSheets;
      this.$store.commit('project/updateHRMTimeSheets', hrmMemberTimeSheets);
    }
  }

  created(): void {
    if (this.mode !== INDIVIDUAL_POP_UP_MODE.ADD) {
      this.currentMember = cloneDeep(this.memberToEdit);
      if (this.isHrmSource(this.currentMember.source)) {
        this.currentInputOption = this.inputOptions.hrm;
      }
    }
  }
}
